import {
	createContext, useCallback, useContext, useLayoutEffect, useRef, useState
} from 'react'
import { createPortal } from 'react-dom'

import type { Content } from '@types'

interface SourceTooltipState {
	excerpt: string;
	index: string;
	position: {
		rect: DOMRect;
	};
	content?: Content | undefined;
	contentId?: string | undefined;
	messageId?: string | undefined;
	imageUrl?: string | undefined;
}

interface SourceTooltipContextType {
	openTooltip: (state: SourceTooltipState) => void;
	closeTooltip: () => void;
	tooltipState: SourceTooltipState | undefined;
}

const SourceTooltipContext = createContext<SourceTooltipContextType | undefined>(undefined)

export function useTooltip() {
	const context = useContext(SourceTooltipContext)

	if (!context) {
		throw new Error('useTooltip must be used within a TooltipProvider')
	}

	return context
}

export function SourceTooltipProvider({ children }: {readonly children: React.ReactNode}) {
	const [tooltipState, setTooltipState] = useState<SourceTooltipState | undefined>(undefined)
	const [tooltipHeight, setTooltipHeight] = useState(0)
	const [tooltipWidth, setTooltipWidth] = useState(0)
	const ref = useRef(null)

	const timeoutRef = useRef<NodeJS.Timeout>()

	const openTooltip = useCallback((state: SourceTooltipState) => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current)
		}

		setTooltipState(state)
	}, [])

	const closeTooltip = useCallback(() => {
		timeoutRef.current = setTimeout(() => {
			setTooltipState(undefined)
		}, 100)
	}, [])

	useLayoutEffect(() => {
		if (ref.current) {
			const element = ref.current as HTMLDivElement
			const { height, width } = element.getBoundingClientRect()

			setTooltipHeight(height)
			setTooltipWidth(width)
		}
	}, [ref, tooltipState])

	// Calculate best position and which CSS properties to use
	const getTooltipPosition = () => {
		if (!tooltipState) {
			return { style: { display: 'none' } }
		}

		const margin = 8
		const edgeMargin = 16
		const { rect } = tooltipState.position
		const style: Record<string, number> = {}

		const fitsBelow = rect.bottom + margin + tooltipHeight + edgeMargin <= window.innerHeight
		const fitsAbove = rect.top - margin - tooltipHeight - edgeMargin >= 0
		const fitsRight = rect.left + tooltipWidth + edgeMargin <= window.innerWidth
		const fitsLeft = rect.right - tooltipWidth - edgeMargin >= 0

		if (fitsBelow) {
			style.top = rect.bottom + margin
		} else if (fitsAbove) {
			style.bottom = window.innerHeight - rect.top + margin
		}

		if (fitsRight) {
			style.left = rect.left
		} else if (fitsLeft) {
			style.right = window.innerWidth - rect.right
		}

		// Clamp to edge margins
		if (typeof style.top === 'number' && style.top < edgeMargin) {
			style.top = edgeMargin
		}

		if (typeof style.left === 'number' && style.left < edgeMargin) {
			style.left = edgeMargin
		}

		if (typeof style.bottom === 'number' && style.bottom < edgeMargin) {
			style.bottom = edgeMargin
		}

		if (typeof style.right === 'number' && style.right < edgeMargin) {
			style.right = edgeMargin
		}

		return { style }
	}

	const tooltipPosition = getTooltipPosition()

	return (
		<SourceTooltipContext.Provider value={ { openTooltip, closeTooltip, tooltipState } }>
			{ children }
			{ tooltipState
				? createPortal(
					<div
						ref={ ref }
						className="fixed fade-in z-50 max-w-[400px] p-4 bg-white border border-gray-100 shadow-xl rounded-lg"
						style={ {
							...tooltipPosition.style
						} }
					>
						{ tooltipState.contentId === 'perplexity'
							? <><div className="text-[16px] font-semibold">
								{ tooltipState.excerpt }
							</div>
							<div className="bg-gray-100 rounded-sm p-3 mt-3 text-sm">Click on <span
								className="bg-blue-100  text-blue-800 rounded-full w-4 h-4 inline-flex items-center
							justify-center text-sm font-bold shadow-sm">{ tooltipState.index }</span> to access source.</div>
							</>
							: <>
								<div className="text-[16px] font-semibold truncate text-gray-800">
									{ tooltipState.content?.title }
								</div>
								{ tooltipState.imageUrl
									? <img
										src={ tooltipState.imageUrl }
										alt="Source preview"
										className="w-full h-auto mb-2 rounded-md"
										style={ { maxHeight: '200px', objectFit: 'cover' } }
									/>
									: null }
								<div className="mt-2 overflow-hidden text-gray-600 text-sm leading-relaxed">
									{ tooltipState.excerpt }
								</div>
								<div className="bg-gray-100 rounded-sm p-3 mt-3 text-sm">Click on <span
									className="bg-blue-100  text-blue-800 rounded-full w-4 h-4 inline-flex items-center
								justify-center text-sm font-bold shadow-sm">{ tooltipState.index }</span> to access source document.</div>
							</> }
					</div>,
					document.body
				)
				: null }
		</SourceTooltipContext.Provider>
	)
}
